import { data, useLoaderData, useNavigate } from 'react-router'
import { BusinessPagination } from '#app/components/business-pagination.tsx'
import { FilterCombobox } from '#app/components/filter-combobox.tsx'
import { RentalCard } from '#app/components/rental-card.tsx'
import { RentalSearch } from '#app/components/rental-search.tsx'
import { Icon } from '#app/components/ui/icon.js'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { pluralize } from '#app/utils/misc.tsx'
import { type Route } from './+types/category.$categorySlug.ts'

const PAGE_SIZE = 36

export async function loader({ request, params }: Route.LoaderArgs) {
	const { categorySlug } = params

	const url = new URL(request.url)
	const pageParam = Number(url.searchParams.get('page') || '1')
	const q = url.searchParams.get('q')?.trim() || ''
	const rawPage =
		Number.isFinite(pageParam) && pageParam > 0 ? Math.floor(pageParam) : 1

	const [category, subcategory] = await Promise.all([
		prisma.category.findUnique({
			where: {
				slug: categorySlug,
				rentalListings: { some: { status: 'published' } },
			},
			select: {
				id: true,
				name: true,
				slug: true,
				subcategories: {
					where: {
						rentalListings: { some: { status: 'published' } },
					},
					select: {
						id: true,
						name: true,
						slug: true,
					},
					orderBy: {
						name: 'asc',
					},
				},
			},
		}),
		prisma.subcategory.findUnique({
			where: {
				slug: categorySlug,
				rentalListings: { some: { status: 'published' } },
			},
			select: {
				id: true,
				name: true,
				slug: true,
				category: {
					select: {
						id: true,
						name: true,
						slug: true,
						subcategories: {
							where: {
								rentalListings: { some: { status: 'published' } },
							},
							select: {
								id: true,
								name: true,
								slug: true,
							},
							orderBy: {
								name: 'asc',
							},
						},
					},
				},
			},
		}),
	])

	if (!category && !subcategory) {
		throw new Response('Category not found', { status: 404 })
	}

	const effectiveCategory = subcategory ? subcategory.category : category
	const subcategories = effectiveCategory?.subcategories || []

	const baseWhere = subcategory
		? {
				subcategories: { some: { id: subcategory.id } },
				status: 'published' as const,
			}
		: {
				categories: { some: { id: effectiveCategory!.id } },
				status: 'published' as const,
			}

	const cities = await prisma.city.findMany({
		where: {
			rentalListings: { some: baseWhere },
		},
		select: {
			id: true,
			name: true,
			slug: true,
		},
		orderBy: {
			name: 'asc',
		},
	})

	// Check if there are any published rental listings with subcategories
	const hasRentalsWithSubcategories =
		subcategories.length > 0
			? await prisma.rentalListing.count({
					where: {
						...baseWhere,
						subcategories: { some: {} },
					},
				})
			: 0

	const where =
		q.length > 0
			? {
					AND: [
						baseWhere,
						{
							OR: [
								{ title: { contains: q, mode: 'insensitive' as const } },
								{ description: { contains: q, mode: 'insensitive' as const } },
							],
						},
					],
				}
			: baseWhere

	const total = await prisma.rentalListing.count({ where })

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
	const page = Math.min(rawPage, totalPages)
	const skip = (page - 1) * PAGE_SIZE

	const rentals = await prisma.rentalListing.findMany({
		where,
		select: {
			id: true,
			title: true,
			slug: true,
			description: true,
			hourlyRateCents: true,
			dailyRateCents: true,
			weeklyRateCents: true,
			monthlyRateCents: true,
			featuredMedia: {
				select: {
					url: true,
					altText: true,
				},
			},
			cities: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			categories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			subcategories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			ownerBusiness: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			ownerUser: {
				select: {
					id: true,
					name: true,
				},
			},
		},
		orderBy: [{ createdAt: 'desc' }, { title: 'asc' }],
		skip,
		take: PAGE_SIZE,
	})

	return data({
		category: effectiveCategory,
		subcategory,
		rentals,
		subcategories,
		cities,
		page,
		pageSize: PAGE_SIZE,
		total,
		hasRentalsWithSubcategories: hasRentalsWithSubcategories > 0,
	})
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb({ data }: { data: Awaited<ReturnType<typeof loader>>['data'] }) {
		const entity = data?.subcategory ?? data?.category
		return entity
			? {
					name: `${pluralize(entity.name)} Rentals`,
					url: `/rentals/category/${entity.slug}`,
				}
			: null
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
	}): SeoProps | null {
		const entity = loaderData?.subcategory ?? loaderData?.category
		if (!entity) return null
		const canonical = `${origin}/rentals/category/${entity.slug}`

		const structuredData =
			loaderData.rentals.length > 0
				? [
						buildItemListLd({
							name: `${pluralize(entity.name)} Rentals`,
							items: loaderData.rentals.map((r) => ({
								name: r.title,
								url: `${origin}/rentals/${r.slug}`,
							})),
						}),
					]
				: undefined

		return {
			title: `${pluralize(entity.name)} Rentals in Dallas-Fort Worth - RentDFW.com`,
			description: `Browse ${pluralize(entity.name.toLowerCase())} rental listings in the Dallas-Fort Worth area. Find equipment and tools available to rent today.`,
			canonical,
			openGraph: {
				title: `${pluralize(entity.name)} Rentals in Dallas-Fort Worth - RentDFW.com`,
				description: `Browse ${pluralize(entity.name.toLowerCase())} rental listings in the Dallas-Fort Worth area.`,
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: `${pluralize(entity.name)} Rentals in Dallas-Fort Worth - RentDFW.com`,
				description: `Browse ${pluralize(entity.name.toLowerCase())} rental listings in the Dallas-Fort Worth area.`,
			},
			structuredData,
		}
	},
}

export default function RentalsCategoryPage() {
	const {
		category,
		subcategory,
		rentals,
		subcategories,
		cities,
		page,
		pageSize,
		total,
		hasRentalsWithSubcategories,
	} = useLoaderData<typeof loader>()
	const navigate = useNavigate()

	if (!category) {
		throw new Error('Category is required')
	}

	const entity = subcategory ?? category

	const currentSubcategoryValue = subcategory ? subcategory.slug : 'all'
	const totalPages = Math.max(1, Math.ceil(total / pageSize))

	function handleSubcategoryChange(value: string) {
		if (!category) return

		if (value === 'all') {
			void navigate(`/rentals/category/${category.slug}`)
		} else {
			void navigate(`/rentals/category/${value}`)
		}
	}

	function handleCityChange(value: string) {
		if (!category) return
		const categorySlug = subcategory ? subcategory.slug : category.slug
		void navigate(`/rentals/${value}/${categorySlug}`)
	}

	return (
		<>
			{/* SEO is handled by SeoHead via handle.seo */}
			<div className="container mx-auto px-4 py-16">
				<div className="mb-8">
					<div className="mb-4 flex flex-col gap-4">
						<div className="mx-auto max-w-3xl text-center">
							<h1 className="text-4xl font-semibold text-balance">
								{entity.name} Rentals in Dallas-Fort Worth
							</h1>
							<p className="text-muted-foreground mt-2 mb-8 text-xl">
								Compare the best {entity.name.toLowerCase()} rentals in
								Dallas-Fort Worth. We make it easy to find high-quality
								inventory from trusted local providers.
							</p>
						</div>

						<div className="flex flex-col gap-3 md:flex-row">
							<div className="w-full">
								<RentalSearch total={total} />
							</div>
							{cities.length > 0 && (
								<div className="w-full md:w-64">
									<FilterCombobox
										options={cities.map((city) => ({
											label: city.name,
											value: city.slug,
										}))}
										onValueChange={handleCityChange}
										placeholder="Filter by city"
										searchPlaceholder="Search cities..."
										emptyText="No cities found"
									/>
								</div>
							)}
							{hasRentalsWithSubcategories && (
								<div className="w-full md:w-64">
									<FilterCombobox
										options={[
											{
												label: `All ${pluralize(category.name)}`,
												value: 'all',
											},
											...subcategories.map((sub) => ({
												label: sub.name,
												value: sub.slug,
											})),
										]}
										value={currentSubcategoryValue}
										onValueChange={handleSubcategoryChange}
										placeholder="Filter by subcategory"
										searchPlaceholder="Search subcategories..."
										emptyText="No subcategories found"
									/>
								</div>
							)}
						</div>
					</div>
				</div>

				{total === 0 ? (
					<div className="bg-muted rounded-lg py-16 text-center">
						<Icon
							name="file-text"
							className="text-muted-foreground mx-auto mb-4 size-12"
						/>
						<h3 className="mb-2 text-xl font-semibold">No Rentals Available</h3>
						<p className="text-muted-foreground">
							No rental listings in this{' '}
							{subcategory ? 'subcategory' : 'category'} at the moment.
						</p>
					</div>
				) : (
					<>
						<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
							{rentals.map((rental) => (
								<RentalCard key={rental.id} rental={rental} />
							))}
						</div>

						<BusinessPagination currentPage={page} totalPages={totalPages} />
					</>
				)}
			</div>
		</>
	)
}
